const STORAGE_KEY = 'skyfurniture_local_catalog_v1';
const ADMIN_SESSION_KEY = 'skyfurniture_local_admin_session';
export const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const LOCAL_ADMIN_PASSWORD = (process.env.REACT_APP_LOCAL_ADMIN_PASSWORD || 'SkyAdmin-2026!').trim();

export const readCatalogChanges = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const records = parsed.records && typeof parsed.records === 'object' ? parsed.records : {};
    return {
      records,
      deletedIds: Array.isArray(parsed.deletedIds) ? parsed.deletedIds : [],
      pendingIds: Array.isArray(parsed.pendingIds) ? parsed.pendingIds.map(String) : Object.keys(records),
      recentExportedIds: Array.isArray(parsed.recentExportedIds) ? parsed.recentExportedIds.map(String).slice(0, 5) : [],
    };
  } catch {
    return { records: {}, deletedIds: [], pendingIds: [], recentExportedIds: [] };
  }
};

const writeCatalogChanges = (changes) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(changes));
  window.dispatchEvent(new CustomEvent('skyfurniture:catalog-changed'));
};

const normalizeCatalogChanges = (changes) => ({
  records: changes?.records && typeof changes.records === 'object' ? changes.records : {},
  deletedIds: Array.isArray(changes?.deletedIds) ? changes.deletedIds.map(String) : [],
  pendingIds: Array.isArray(changes?.pendingIds) ? changes.pendingIds.map(String) : [],
  recentExportedIds: Array.isArray(changes?.recentExportedIds) ? changes.recentExportedIds.map(String).slice(0, 5) : [],
});

export const createCatalogExport = () => ({
  format: 'skyfurniture-dashboard-catalog',
  version: 1,
  exportedAt: new Date().toISOString(),
  records: readCatalogChanges().records,
  deletedIds: readCatalogChanges().deletedIds,
});

export const markCatalogChangesExported = () => {
  const changes = readCatalogChanges();
  const changedIds = [...changes.pendingIds, ...changes.deletedIds].map(String);
  const recentExportedIds = Array.from(new Set([...changedIds, ...changes.recentExportedIds])).slice(0, 5);
  writeCatalogChanges({ ...changes, pendingIds: [], recentExportedIds });
};

export const importCatalogChanges = (catalogueFile) => {
  if (!catalogueFile || catalogueFile.format !== 'skyfurniture-dashboard-catalog' || catalogueFile.version !== 1) {
    throw new Error('This is not a valid Sky Furniture dashboard catalogue file.');
  }
  const normalized = normalizeCatalogChanges(catalogueFile);
  writeCatalogChanges({ ...normalized, pendingIds: [], recentExportedIds: Object.keys(normalized.records).slice(-5).reverse() });
};

export const applyPublishedCatalogChanges = (catalogueFile) => {
  if (!catalogueFile || catalogueFile.format !== 'skyfurniture-dashboard-catalog' || catalogueFile.version !== 1) return { records: {}, deletedIds: [], pendingIds: [], recentExportedIds: [] };
  return normalizeCatalogChanges(catalogueFile);
};

export const mergeCatalogChanges = (baseProducts) => {
  const { records, deletedIds } = readCatalogChanges();
  return mergeCatalogWithChanges(baseProducts, { records, deletedIds });
};

export const getNextDashboardProductId = (baseProducts = []) => {
  const localIds = Object.keys(readCatalogChanges().records).map((id) => Number(id) || 0);
  const baseIds = baseProducts.map((product) => Number(product.id) || 0);
  return Math.max(0, ...baseIds, ...localIds) + 1;
};

export const mergeCatalogWithChanges = (baseProducts, changes) => {
  const { records, deletedIds } = normalizeCatalogChanges(changes);
  const deleted = new Set(deletedIds.map(String));
  const baseIds = new Set(baseProducts.map((product) => String(product.id)));
  const merged = baseProducts
    .filter((product) => !deleted.has(String(product.id)))
    .map((product) => ({ ...product, ...(records[String(product.id)] || {}) }));

  Object.entries(records).forEach(([id, record]) => {
    if (!baseIds.has(String(id)) && !deleted.has(String(id))) merged.push(record);
  });
  return merged;
};

export const saveProductRecord = (product) => {
  const changes = readCatalogChanges();
  const id = String(product.id);
  changes.records[id] = { ...product, updatedAt: new Date().toISOString() };
  changes.deletedIds = changes.deletedIds.filter((deletedId) => String(deletedId) !== id);
  changes.pendingIds = Array.from(new Set([...changes.pendingIds, id]));
  changes.recentExportedIds = changes.recentExportedIds.filter((exportedId) => exportedId !== id);
  writeCatalogChanges(changes);
};

export const removeProductRecord = (productId) => {
  const changes = readCatalogChanges();
  const id = String(productId);
  changes.deletedIds = Array.from(new Set([...changes.deletedIds, id]));
  delete changes.records[id];
  changes.pendingIds = Array.from(new Set([...changes.pendingIds, id]));
  changes.recentExportedIds = changes.recentExportedIds.filter((exportedId) => exportedId !== id);
  writeCatalogChanges(changes);
};

// Display-only cache of deleted product names, so the dashboard can show a
// "Deleted products" list (with restore) even though deleted products no
// longer appear in the catalogue. Not part of the catalogue export format.
const DELETED_META_KEY = 'skyfurniture_local_catalog_deleted_meta_v1';

export const rememberDeletedProduct = (product) => {
  try {
    const meta = JSON.parse(localStorage.getItem(DELETED_META_KEY) || '{}');
    meta[String(product.id)] = {
      name: product.name || '',
      category: product.category || '',
      subcategory: product.subcategory || '',
    };
    localStorage.setItem(DELETED_META_KEY, JSON.stringify(meta));
  } catch {
    // Display cache only — never block a deletion on it.
  }
};

export const getDeletedProductMeta = () => {
  try {
    return JSON.parse(localStorage.getItem(DELETED_META_KEY) || '{}');
  } catch {
    return {};
  }
};

export const forgetDeletedProduct = (productId) => {
  try {
    const meta = JSON.parse(localStorage.getItem(DELETED_META_KEY) || '{}');
    delete meta[String(productId)];
    localStorage.setItem(DELETED_META_KEY, JSON.stringify(meta));
  } catch {
    // Display cache only.
  }
};

export const resetProductOverride = (productId) => {
  const changes = readCatalogChanges();
  const id = String(productId);
  delete changes.records[id];
  changes.deletedIds = changes.deletedIds.filter((deletedId) => String(deletedId) !== id);
  changes.pendingIds = Array.from(new Set([...changes.pendingIds, id]));
  changes.recentExportedIds = changes.recentExportedIds.filter((exportedId) => exportedId !== id);
  writeCatalogChanges(changes);
};

export const isAdminSessionActive = () => sessionStorage.getItem(ADMIN_SESSION_KEY) === 'active';
export const setAdminSession = (active) => {
  if (active) sessionStorage.setItem(ADMIN_SESSION_KEY, 'active');
  else sessionStorage.removeItem(ADMIN_SESSION_KEY);
};

export const normalizeImageFile = (file, maxEdge = 1600, quality = 0.88) => new Promise((resolve, reject) => {
  if (!file || !SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    reject(new Error('Please choose a JPEG, PNG, or WebP image.'));
    return;
  }
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const outputType = SUPPORTED_IMAGE_TYPES.includes(file.type) ? file.type : 'image/jpeg';
    const context = canvas.getContext('2d');
    if (outputType !== 'image/png') {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(objectUrl);
    resolve({
      dataUrl: canvas.toDataURL(outputType, quality),
      mimeType: outputType,
      width: canvas.width,
      height: canvas.height,
      name: file.name,
    });
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error(`Could not read ${file.name}.`));
  };
  image.src = objectUrl;
});

export const createEmptyProduct = (nextId) => ({
  id: nextId,
  name: '',
  description: '',
  altText: '',
  category: 'dressers',
  subcategory: '',
  priceCAD: '',
  image: '',
  images: [],
  inStock: true,
  stockCount: 15,
  dimensions: { width: '', height: '', depth: '' },
  availableProvinces: [],
  availableCities: [],
  provincePrices: {},
  deliveryOptions: { delivery: true, pickup: true },
  colors: [],
});
